// kfe-line-chart.js — hand-rolled inline-SVG line graph (Brief 097).
//
// PORT TARGET: Classes/KFEModules/Reports/ChartViews/KFELineChartView (iOS).
//
// No Chart.js, no D3 — direct SVG, same posture as the squiggle (CLAUDE.md
// two-runtime-deps rule). Used by the Lesson Report's "Report on Performance"
// section for the HAAI Recognition Index per-test-instance series.
//
// Props:
//   points:     Array<{ x: number|string, y: number }>
//   yRange:     [number, number]      (default [0, 100] — fixed % scale)
//   yLabel:     string                (optional, rotated at the left)
//   xLabel:     string                (optional, centred under the axis)
//   gridlinesY: Array<number>         (default [20, 40, 60, 80, 100])
//
// Renders: horizontal gridlines + Y tick labels, the line, a marker at each
// point, the value label at each point, and optional axis captions.
// Responsive via viewBox; empty points → empty chart, no crash.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";

const NS = "http://www.w3.org/2000/svg";

const VB_W = 320;
const VB_H = 220;
const PAD_LEFT = 34;   // Y tick labels + caption
const PAD_RIGHT = 14;
const PAD_TOP = 18;
const PAD_BOTTOM = 34;  // X labels + caption
const PLOT_X = PAD_LEFT;
const PLOT_W = VB_W - PAD_LEFT - PAD_RIGHT;
const PLOT_TOP = PAD_TOP;
const PLOT_BOTTOM = VB_H - PAD_BOTTOM;
const PLOT_H = PLOT_BOTTOM - PLOT_TOP;

const DEFAULT_GRIDLINES = [20, 40, 60, 80, 100];

/** @param {SVGElement} parent @param {string} tag @param {Record<string,string>} attrs */
function el(parent, tag, attrs) {
  const node = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  parent.appendChild(node);
  return node;
}

export class KFELineChart extends KFEComponent {
  static props = ["points", "yRange", "yLabel", "xLabel", "gridlinesY"];
  static styles = `
    :host { display: block; }
    svg { width: 100%; height: auto; display: block; }
    .grid { stroke: var(--kfe-border); stroke-width: 1; }
    .axis { stroke: var(--kfe-border); stroke-width: 1; }
    .series { fill: none; stroke: var(--kfe-accent); stroke-width: 2; vector-effect: non-scaling-stroke; }
    .marker { fill: var(--kfe-accent); }
    .pt-label { fill: var(--kfe-fg); font-size: 10px; font-weight: 600; text-anchor: middle; }
    .tick-label { fill: var(--kfe-muted); font-size: 9px; text-anchor: end; }
    .x-label { fill: var(--kfe-muted); font-size: 9px; text-anchor: middle; }
    .caption { fill: var(--kfe-muted); font-size: 10px; text-anchor: middle; }
  `;

  render() {
    const points = /** @type {{x:number|string,y:number}[]} */ (
      /** @type {any} */ (this).points
    ) ?? [];
    const yRangeProp = /** @type {[number,number]|undefined} */ (/** @type {any} */ (this).yRange);
    const [yMin, yMax] = Array.isArray(yRangeProp) && yRangeProp.length === 2
      ? yRangeProp : [0, 100];
    const yLabel = /** @type {string|undefined} */ (/** @type {any} */ (this).yLabel);
    const xLabel = /** @type {string|undefined} */ (/** @type {any} */ (this).xLabel);
    const gridlines = /** @type {number[]|undefined} */ (/** @type {any} */ (this).gridlinesY)
      ?? DEFAULT_GRIDLINES;

    const svg = document.createElementNS(NS, "svg");
    svg.setAttribute("viewBox", `0 0 ${VB_W} ${VB_H}`);
    svg.setAttribute("role", "img");
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

    const range = (yMax - yMin) || 1;
    const yToPx = (/** @type {number} */ v) =>
      PLOT_BOTTOM - ((v - yMin) / range) * PLOT_H;

    // Gridlines + Y tick labels (drawn even when empty, so the % scale reads).
    for (const g of gridlines) {
      const y = yToPx(g);
      el(svg, "line", {
        class: "grid",
        x1: String(PLOT_X), y1: y.toFixed(2),
        x2: String(PLOT_X + PLOT_W), y2: y.toFixed(2),
      });
      el(svg, "text", {
        class: "tick-label",
        x: String(PLOT_X - 4), y: (y + 3).toFixed(2),
      }).textContent = `${g}%`;
    }

    // Left axis.
    el(svg, "line", {
      class: "axis",
      x1: String(PLOT_X), y1: String(PLOT_TOP),
      x2: String(PLOT_X), y2: String(PLOT_BOTTOM),
    });

    if (yLabel) {
      const cy = PLOT_TOP + PLOT_H / 2;
      el(svg, "text", {
        class: "caption",
        x: "10", y: String(cy),
        transform: `rotate(-90 10 ${cy})`,
      }).textContent = yLabel;
    }

    if (points.length > 0) {
      const n = points.length;
      // Single point sits centred; multiple points span the plot width.
      const xToPx = (/** @type {number} */ i) =>
        n === 1 ? PLOT_X + PLOT_W / 2 : PLOT_X + (i / (n - 1)) * PLOT_W;

      const coords = points.map((p, i) => ({
        px: xToPx(i),
        py: yToPx(Number.isFinite(p.y) ? p.y : yMin),
        p,
      }));

      if (coords.length >= 2) {
        const d = "M " + coords.map((c) => `${c.px.toFixed(2)},${c.py.toFixed(2)}`).join(" L ");
        el(svg, "path", { class: "series", d });
      }

      for (const c of coords) {
        el(svg, "circle", {
          class: "marker",
          cx: c.px.toFixed(2), cy: c.py.toFixed(2), r: "3",
        });
        // Value label above the marker (clamped below the top edge).
        const labelY = Math.max(PLOT_TOP + 8, c.py - 7);
        el(svg, "text", {
          class: "pt-label",
          x: c.px.toFixed(2), y: labelY.toFixed(2),
        }).textContent = `${formatValue(c.p.y)}%`;
        // X-axis label.
        el(svg, "text", {
          class: "x-label",
          x: c.px.toFixed(2), y: String(PLOT_BOTTOM + 12),
        }).textContent = String(c.p.x);
      }
    }

    if (xLabel) {
      el(svg, "text", {
        class: "caption",
        x: String(PLOT_X + PLOT_W / 2), y: String(VB_H - 4),
      }).textContent = xLabel;
    }

    return html`${svg}`;
  }
}

function formatValue(v) {
  if (!Number.isFinite(v)) return "0";
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}
